/**
 * Description: Capability-driven FSM; uses injected registry and engine references (no globals).
 * Usage: new BehaviorEngine(registry); init({ config, movement, environment }); update(dtMs).
 */
import { CapabilityRegistry } from './capability-registry.js';

export class BehaviorEngine {
  constructor(registry) {
    this._registry = registry;
    this._config = null;
    this._activeCaps = {};
    this._currentState = 'idle';
    this._paused = false;
    this._freqMod = 1;
    this._movement = null;
    this._environment = null;

    this._ctx = {
      movement: null,
      environment: null,
      config: null,
      stateTimer: 0,
      stateTime: 0,
      walkDir: 0,
      visualState: null,
      transition: (s) => this._transition(s),
      transitionToNext: () => this._transitionToNext(),
      getStateDuration: (n) => this._getStateDuration(n),
      randomRange: (min, max) => min + Math.random() * (max - min),
    };
  }

  _randomRange(min, max) {
    return min + Math.random() * (max - min);
  }

  _getStateDuration(stateName) {
    const s = (this._config.states || {})[stateName] || {};
    const minD = s.minDuration || 2000;
    const maxD = s.maxDuration || 5000;
    return this._randomRange(minD, maxD) * this._freqMod;
  }

  _transitionToNext() {
    const table = (this._config.transitions || {})[this._currentState];
    if (!table) {
      this._transition('idle');
      return;
    }

    const filtered = Object.create(null);
    let total = 0;
    for (const key in table) {
      if (Object.prototype.hasOwnProperty.call(table, key) && this._activeCaps[key]) {
        filtered[key] = table[key];
        total += table[key];
      }
    }

    if (total === 0) {
      this._transition('idle');
      return;
    }

    const roll = Math.random() * total;
    let cumulative = 0;
    for (const key in filtered) {
      if (Object.prototype.hasOwnProperty.call(filtered, key)) {
        cumulative += filtered[key];
        if (roll <= cumulative) {
          this._transition(key);
          return;
        }
      }
    }
    this._transition('idle');
  }

  _transition(newState) {
    if (!this._activeCaps[newState]) newState = 'idle';

    const current = this._activeCaps[this._currentState];
    if (current && current.exit) current.exit(this._ctx);

    this._currentState = newState;
    this._ctx.stateTime = 0;
    this._ctx.stateTimer = 0;
    this._ctx.visualState = null;

    const next = this._activeCaps[this._currentState];
    if (next && next.enter) next.enter(this._ctx);
  }

  init({ config, movement, environment }) {
    this._config = config;
    this._paused = false;
    this._freqMod = 2.0 - (((config.behaviorFrequency || 50) - 1) / 99) * 1.7;

    this._movement = movement;
    this._environment = environment;
    this._ctx.movement = movement;
    this._ctx.environment = environment;
    this._ctx.config = config;

    const charCaps = config.capabilities || CapabilityRegistry.getDefaultList();
    const enabledCaps = config.enabledCapabilities || [];

    this._activeCaps = this._registry.resolve(charCaps, enabledCaps);

    this._transition('idle');
  }

  update(dtMs) {
    if (this._paused) return;
    this._ctx.stateTime += dtMs;
    const cap = this._activeCaps[this._currentState];
    if (cap && cap.update) cap.update(this._ctx, dtMs);
  }

  getState() {
    return this._ctx.visualState || this._currentState;
  }

  pause() {
    this._paused = true;
    if (this._movement) this._movement.stopWalk();
  }

  resume() {
    if (!this._paused) return;
    this._paused = false;
  }

  getActiveCaps() {
    return { ...this._activeCaps };
  }
}
